import React, { useState, useMemo } from 'react';
import { AlertTriangle, ShieldCheck, Info, TrendingUp, Users, Clock, Calculator } from 'lucide-react';

function RiskForm({ datos, onChange }) {
  return (
    <div className="lg:col-span-5 bg-white border border-slate-200 p-8" data-testid="calculator-form">
      <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-slate-900">
        <Calculator size={20} className="text-rose-600" />
        Parámetros del caso
      </h3>
      <div className="space-y-6">
        <div className="form-group">
          <label className="form-label flex items-center gap-2"><Users size={14} /> Tamaño de plantilla</label>
          <input type="number" value={datos.numTrabajadores} onChange={(e) => onChange('numTrabajadores', e.target.value)} className="form-input" min="1" data-testid="input-trabajadores" />
          {datos.numTrabajadores < 25 && <p className="mt-2 text-xs text-rose-600 font-medium">PYME sensible (&lt;25 trabajadores)</p>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="form-group">
            <label className="form-label">Salario bruto mensual</label>
            <div className="relative">
              <input type="number" value={datos.salarioBrutoMensual} onChange={(e) => onChange('salarioBrutoMensual', e.target.value)} className="form-input pr-8" min="0" data-testid="input-salario" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Antigüedad (años)</label>
            <input type="number" value={datos.antiguedadAnios} onChange={(e) => onChange('antiguedadAnios', e.target.value)} className="form-input" min="0" data-testid="input-antiguedad" />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label flex items-center gap-2"><Clock size={14} /> Duración de la baja (meses)</label>
          <input type="number" value={datos.duracionBajaMeses} onChange={(e) => onChange('duracionBajaMeses', e.target.value)} className="form-input" min="0" data-testid="input-baja" />
        </div>
        <div className="form-group">
          <label className="form-label flex items-center gap-2"><TrendingUp size={14} /> Coste estimado de adaptación</label>
          <div className="relative">
            <input type="number" value={datos.costeAdaptacion} onChange={(e) => onChange('costeAdaptacion', e.target.value)} className="form-input pr-8" min="0" data-testid="input-coste" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
          </div>
        </div>
        <div className="pt-6 border-t border-slate-200 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input type="checkbox" checked={datos.tieneInformeDetallado} onChange={(e) => onChange('tieneInformeDetallado', e.target.checked)} className="form-checkbox mt-0.5" data-testid="checkbox-informe" />
            <span className="text-sm text-slate-700 group-hover:text-slate-900">¿El informe del SPA detalla limitaciones funcionales concretas?</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input type="checkbox" checked={datos.haSolicitadoPuesto} onChange={(e) => onChange('haSolicitadoPuesto', e.target.checked)} className="form-checkbox mt-0.5" data-testid="checkbox-puesto" />
            <span className="text-sm text-slate-700 group-hover:text-slate-900">¿Se ha solicitado la descripción del puesto al SPA?</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function RiskResults({ analisis, datos }) {
  return (
    <div className="lg:col-span-7 space-y-6" data-testid="calculator-results">
      <div className={`result-card p-8 risk-${analisis.nivelRiesgo}`} data-testid="risk-result">
        <div className="flex justify-between items-start mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Diagnóstico de riesgo legal</span>
            <h3 className="text-2xl font-bold mt-2 text-slate-900">Análisis en tiempo real</h3>
          </div>
          {analisis.nivelRiesgo === 'high' ? <AlertTriangle className="text-rose-600" size={32} /> :
           analisis.nivelRiesgo === 'medium' ? <AlertTriangle className="text-amber-500" size={32} /> :
           <ShieldCheck className="text-emerald-600" size={32} />}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-slate-50 p-4 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Nivel de Riesgo (LISOS)</p>
            <p className={`text-lg font-bold ${analisis.nivelRiesgo === 'high' ? 'text-rose-600' : analisis.nivelRiesgo === 'medium' ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="risk-level">{analisis.riesgoSancion}</p>
          </div>
          <div className="bg-slate-50 p-4 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Exposición económica</p>
            <p className="text-lg font-bold text-slate-900" data-testid="risk-amount">{analisis.multaEstimada}</p>
          </div>
        </div>
      </div>

      {analisis.esPymeSensible && (
        <div className="result-card p-6" data-testid="pyme-result">
          <h4 className="font-bold flex items-center gap-2 text-slate-900 mb-4">
            <Info size={18} className="text-rose-600" /> Aplicación Ley 2/2025 (PYME)
          </h4>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Límite de <strong>carga excesiva</strong> para ajustes razonables:</p>
            <p className="text-3xl font-bold text-slate-900">{analisis.limiteLegal.toLocaleString('es-ES')} €</p>
            <div className="text-xs text-slate-500 space-y-1">
              <p>6 mensualidades: {analisis.seisMensualidades.toLocaleString('es-ES')} €</p>
              <p>Indemnización estimada: {analisis.indemnizacionEstimada.toLocaleString('es-ES')} €</p>
            </div>
            {analisis.esCargaExcesiva ? (
              <div className="mt-4 p-3 bg-rose-50 border border-rose-200">
                <p className="text-sm font-semibold text-rose-700">La adaptación se considera legalmente excesiva. Puede motivar la extinción del contrato.</p>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200">
                <p className="text-sm font-semibold text-emerald-700">La adaptación es económicamente viable. El despido podría ser declarado nulo.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="result-card p-6">
        <h4 className="font-bold text-slate-900 mb-4">Recomendaciones inmediatas</h4>
        <ul className="space-y-3">
          {!datos.tieneInformeDetallado && (
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-600 mt-2 flex-shrink-0" />
              Solicite al SPA un informe detallado con limitaciones funcionales específicas
            </li>
          )}
          {!datos.haSolicitadoPuesto && (
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-600 mt-2 flex-shrink-0" />
              Requiera la descripción detallada del puesto de trabajo al SPA
            </li>
          )}
          {datos.duracionBajaMeses >= 6 && (
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
              Baja superior a 6 meses: documente exhaustivamente todo el proceso
            </li>
          )}
          <li className="flex items-start gap-2 text-sm text-slate-600">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2 flex-shrink-0" />
            Consulte con un especialista para validar su protocolo de reincorporación
          </li>
        </ul>
      </div>
    </div>
  );
}

// Legal constants (LISOS / Ley 2/2025)
const PYME_THRESHOLD = 25;
const DAYS_IN_YEAR = 365;
const MONTHS_SALARY_LIMIT = 6;
const INDEMNIZATION_DAYS_PER_YEAR = 33;
const LONG_LEAVE_MONTHS = 6;

export default function RiskCalculator() {
  const [datos, setDatos] = useState({
    numTrabajadores: 10,
    salarioBrutoMensual: 2000,
    antiguedadAnios: 5,
    costeAdaptacion: 15000,
    duracionBajaMeses: 7,
    tieneInformeDetallado: false,
    haSolicitadoPuesto: false
  });

  const analisis = useMemo(() => {
    const seisMensualidades = datos.salarioBrutoMensual * MONTHS_SALARY_LIMIT;
    const salarioDiario = (datos.salarioBrutoMensual * 12) / DAYS_IN_YEAR;
    const indemnizacionEstimada = salarioDiario * INDEMNIZATION_DAYS_PER_YEAR * datos.antiguedadAnios;
    const limiteLegal = Math.max(seisMensualidades, indemnizacionEstimada);
    const esPymeSensible = datos.numTrabajadores < PYME_THRESHOLD;
    const esCargaExcesiva = esPymeSensible && datos.costeAdaptacion > limiteLegal;

    let riesgoSancion = "Bajo";
    let multaEstimada = "0 €";
    let nivelRiesgo = "low";

    if (datos.duracionBajaMeses >= LONG_LEAVE_MONTHS && !datos.haSolicitadoPuesto) {
      riesgoSancion = "Muy grave (Obstrucción/Omisión)";
      multaEstimada = "Hasta 983.736 €";
      nivelRiesgo = "high";
    } else if (!datos.tieneInformeDetallado) {
      riesgoSancion = "Grave (Vigilancia insuficiente)";
      multaEstimada = "Hasta 49.180 €";
      nivelRiesgo = "medium";
    }

    return { limiteLegal, esCargaExcesiva, riesgoSancion, multaEstimada, esPymeSensible, nivelRiesgo, seisMensualidades, indemnizacionEstimada };
  }, [datos]);

  const handleInputChange = (field, value) => {
    setDatos(prev => ({
      ...prev,
      [field]: field.includes('tiene') || field.includes('haSolicitado') ? value : Number(value)
    }));
  };

  return (
    <section id="analizador" className="py-24 px-6 bg-slate-50" data-testid="risk-calculator">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-600">Herramienta de diagnóstico</span>
          <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 text-slate-900">Analizador de riesgo LISOS</h2>
          <p className="mt-4 text-lg text-slate-600">Evalúe en tiempo real el nivel de exposición legal de su empresa ante una reincorporación tras baja prolongada.</p>
        </div>
        <div className="grid lg:grid-cols-12 gap-8">
          <RiskForm datos={datos} onChange={handleInputChange} />
          <RiskResults analisis={analisis} datos={datos} />
        </div>
      </div>
    </section>
  );
}
